import { permissionsMappings } from '@lmt/unicorn-shared/app/api/auth/token-decoder';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { RoleDto } from '@lmt/shared-types';
import { ScrollToTop } from '@lmt/unicorn-shared/app/components/common/table/helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import React, { FC, useEffect, useRef } from 'react';
import { localizePermission } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import { snakeCase } from 'lodash';

import styles from '@lmt/unicorn-shared/app/components/patients/list/components/PatientsTable.module.scss';
import { TableDotMenu } from '../../../common/table/TableDotMenu';

/**
 * List of mappings which we do not display
 */
export const permissionDisplayFilter = [
  permissionsMappings.generateTestData,
  permissionsMappings.mockEgkCardReader,
  permissionsMappings.editDataBlockTypesAndFields,
  permissionsMappings.accessToAllSubtenants,
  permissionsMappings.manageNotes,
  permissionsMappings.editPersonalDashboardUIConfiguration,
  permissionsMappings.editPersonalPatientOverviewUIConfiguration,
  permissionsMappings.rolesPageWithPermissions,
];

type Props = {
  noResults: boolean;
  data: RoleDto[];
  onDelete: (roleId: number) => void;
  onEdit: (roleId: number, isDefault?: boolean) => void;
};

const nameof = nameofFactory<RoleDto>();

const columnStyles = {
  [nameof('name')]: {
    minWidth: '200px',
    width: '30%',
    sortable: false,
  },
  [nameof('permissions')]: {
    minWidth: '60px',
    width: '70%',
    sortable: false,
  },
  ['actions']: {
    minWidth: '10px',
    width: '0.1%',
  },
};

export const RolesTable: FC<Props> = React.memo(function RolesTable(props) {
  const i18n = useScopedTranslation('roles.list');

  const tableRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (tableRef.current !== null) {
      ScrollToTop(tableRef.current);
    }
  }, [props.data]);

  return (
    <TableContainer
      ref={tableRef}
      className={styles.tableContainer}
      component={Paper}
    >
      <Table stickyHeader className={styles.table}>
        <TableHead>
          <TableRow>
            {Object.keys(columnStyles).map((columnKey) => (
              <TableCell
                key={`${columnKey}`}
                onClick={() => columnStyles[columnKey].sortable}
                className={styles.header}
                style={columnStyles[columnKey]}
              >
                <div className={styles.headerCell}>
                  {i18n.t(`columns.${columnKey}` as any)}
                </div>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {props.data.map((role) => (
            <TableRow
              key={role.id}
              hover
              onClick={() =>
                props.onEdit(role.id, role.isDefault || !role.isEditable)
              }
            >
              <TableCell>
                <div className={styles.titleCell}>
                  {i18n.t(`default_roles.${snakeCase(role.name)}`, role.name)}
                </div>
              </TableCell>
              <TableCell>
                <div className={styles.listCell}>
                  {role.permissions &&
                    role.permissions
                      .filter(
                        (permission) =>
                          !permissionDisplayFilter.includes(permission),
                      )
                      .map((permission) => localizePermission(permission))
                      .sort((a, b) => a.localeCompare(b))
                      .join(', ')}
                </div>
              </TableCell>

              <TableCell
                className={styles.optionCell}
                onClick={(event: any) => event.stopPropagation()}
              >
                <TableDotMenu
                  options={[
                    {
                      title: i18n.t('options.delete_button'),
                      onClick: () => props.onDelete(role.id),
                      isDeleteItem: true,
                      disabled: !role.isEditable || role.isDefault,
                    },
                  ]}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
